<template>
  <main class="ws">

<!-- ============ 首屏 ============ -->
<section class="ph ph--ink ph--center">
  <div class="wrap">
    <span class="ph__kicker">{{ $t("AI 员工") }}</span>
    <h1 class="ph__title">{{ $t("18 位 AI 员工") }}<br>{{ $t("最忙的是拉流量那几位") }}</h1>
    <p class="ph__sub">{{ $t("商品有人采、图片有人翻、订单有人盯——这些都不难。真正决定一间店活不活得下去的，是") }}<b>{{ $t("有没有人来") }}</b>{{ $t("。所以增长组的 5 位员工，天天在做的就是这件事：先把店修到能接住流量，再把流量一处一处引过来。") }}</p>
    <div class="btns" style="justify-content:center">
      <router-link class="btn btn--primary" to="/sign-up">{{ $t("免费开店，让 AI 员工先体检一遍") }}</router-link>
      <a class="btn btn--line" href="#automarketing">{{ $t("看自动营销怎么跑") }}</a>
    </div>
    <p class="ph__meta">
      <i><svg class="ico"><use href="#i-life"/></svg>{{ $t("18 位员工，四组分工") }}</i>
      <i><svg class="ico"><use href="#i-horn"/></svg>{{ $t("9 个内容渠道") }}</i>
      <i><svg class="ico"><use href="#i-check"/></svg>{{ $t("12 项店铺体检") }}</i>
      <i><svg class="ico"><use href="#i-mail"/></svg>{{ $t("4 条自动序列") }}</i>
    </p>
  </div>
</section>

<div class="guide-band guide-band--after-ink">
  <div class="wrap">
    <div class="guide guide--center">
      <img class="guide__m" src="/website/img/tao-point.png" alt="">
      <div class="bubble">{{ $t("这页分两半。前半说") }}<b>{{ $t("流量是怎么被拉进来的") }}</b>{{ $t("，后半是 18 位员工分别管什么。赶时间就直接往下翻到「30 天」。") }}</div>
    </div>
  </div>
</div>

<!-- ============ 数据 ============ -->
<section class="sec sec--tight">
  <div class="wrap">
    <div class="metrics">
      <div class="metric"><b>18</b><span>{{ $t("位 AI 员工，分四组") }}</span></div>
      <div class="metric"><b>9</b><span>{{ $t("个内容渠道一件商品全铺") }}</span></div>
      <div class="metric"><b>12</b><span>{{ $t("项店铺体检，按浪费钱排序") }}</span></div>
      <div class="metric"><b>4</b><span>{{ $t("条自动营销序列") }}</span></div>
    </div>
  </div>
</section>

<!-- ============ 01 流量是被挡住的 ============ -->
<section class="sec sec--gray">
  <div class="wrap">
    <div class="sec__head">
      <span class="eyebrow">{{ $t("先找堵点") }}</span>
      <h2 class="sec__title">{{ $t("流量不是没来，是被挡住了") }}</h2>
      <p class="sec__sub">{{ $t("很多店花了力气发内容，还是没单。问题常常不在内容，而在「页面接不住」——标题是中文、没主图、没描述、价格离谱。这些不修，来多少人都一样。") }}</p>
    </div>

    <div class="frow">
      <div>
        <span class="frow__tag">{{ $t("12 项体检，按「会不会浪费钱」排序") }}</span>
        <h3 class="frow__t">{{ $t("先花三分钟，把店过一遍") }}</h3>
        <p class="frow__d">{{ $t("「推广总控」打开就跑一次体检：不是给你一张漂亮的分数表，而是直接告诉你哪几项") }}<b>{{ $t("现在推就等于扔钱") }}</b>{{ $t("，该修的先修，能放的最后再说。") }}</p>
        <ul class="frow__list">
          <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("阻塞项 / 建议修 / 已通过，三档分清") }}</span></li>
          <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("每一项都写清楚「不修的后果」，不是打分了事") }}</span></li>
          <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("体检完直接收敛到 15 个商品，不让你 65 个一起推") }}</span></li>
        </ul>
        <div class="note" style="margin-top:20px">
          <svg class="ico"><use href="#i-chart"/></svg>
          <span>{{ $t("体检用的是你店里的真实数据：商品数、SEO 地址、主图、描述、分类、价格分布、买家数。") }}<b>{{ $t("不是模板打分") }}</b>{{ $t("。") }}</span>
        </div>
      </div>

      <div class="frow__media">
        <div class="media-card">
          <div class="chk">
            <div class="chk__hd">
              <div>
                <b>{{ $t("店铺体检") }}</b>
                <span>{{ $t("刚刚 · 65 个在售商品") }}</span>
              </div>
              <em class="chk__badge">{{ $t("3 项待修") }}</em>
            </div>

            <div class="chk__row is-block">
              <span class="chk__dot"></span>
              <div>
                <div class="chk__t">{{ $t("商品标题语言") }} <em>{{ $t("阻塞") }}</em></div>
                <p class="chk__d">{{ $t("52 / 65 个标题疑似中文，而站点语言是 Deutsch") }}</p>
                <p class="chk__fix">{{ $t("先改主推那 10 个，不用 65 个一起改 →") }}</p>
              </div>
            </div>

            <div class="chk__row is-block">
              <span class="chk__dot"></span>
              <div>
                <div class="chk__t">{{ $t("商品主图") }} <em>{{ $t("阻塞") }}</em></div>
                <p class="chk__d">{{ $t("只有 41 / 65 个商品有主图") }}</p>
                <p class="chk__fix">{{ $t("没图的既进不了 Merchant Feed，也做不了 Pinterest") }}</p>
              </div>
            </div>

            <div class="chk__row is-warn">
              <span class="chk__dot"></span>
              <div>
                <div class="chk__t">{{ $t("价格合理性") }} <em>{{ $t("建议修") }}</em></div>
                <p class="chk__d">{{ $t("2 个价格明显异常（最高 ¥38,600）") }}</p>
                <p class="chk__fix">{{ $t("大概率是导入时汇率算错，逐个改回合理价") }}</p>
              </div>
            </div>

            <div class="chk__row is-ok">
              <span class="chk__dot"></span>
              <div>
                <div class="chk__t">{{ $t("品类归属") }} <em>{{ $t("通过") }}</em></div>
                <p class="chk__d">{{ $t("品类集中在 3 个类目") }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="tbl-wrap" style="margin-top:44px">
      <table class="tbl">
        <thead>
          <tr><th>{{ $t("体检项") }}</th><th>{{ $t("卡住会怎样") }}</th><th>{{ $t("谁来修") }}</th></tr>
        </thead>
        <tbody>
          <tr><td>{{ $t("在售商品") }}</td><td>{{ $t("可正常打开的商品不到 5 个，投多少流量都白搭") }}</td><td>{{ $t("你上架，巡检官列缺项") }}</td></tr>
          <tr><td>{{ $t("商品标题语言") }}</td><td>{{ $t("站点是德语、标题还是中文，当地人根本搜不到") }}</td><td>{{ $t("翻译本地化官") }}</td></tr>
          <tr><td>{{ $t("SEO 描述") }}</td><td>{{ $t("搜索结果里没摘要，收录会慢上好几周") }}</td><td>{{ $t("SEO 优化师") }}</td></tr>
          <tr><td>{{ $t("商品主图") }}</td><td>{{ $t("进不了 Merchant Feed，也做不了 Pinterest") }}</td><td>{{ $t("图片处理官") }}</td></tr>
          <tr><td>{{ $t("品类归属") }}</td><td>{{ $t("分不清卖什么，就选不了渠道和人群") }}</td><td>{{ $t("你归类，AI 给建议") }}</td></tr>
          <tr><td>{{ $t("价格合理性") }}</td><td>{{ $t("汇率算错留个「¥38,600」的杯子，来人也转化不了") }}</td><td>{{ $t("定价策略官") }}</td></tr>
          <tr><td>{{ $t("库存") }}</td><td>{{ $t("推爆了发现没货，等于白推一轮") }}</td><td>{{ $t("订单管家") }}</td></tr>
          <tr><td>{{ $t("SEO 地基") }}</td><td>{{ $t("没有 sitemap，搜索引擎不知道你有多少页") }}</td><td>{{ $t("平台自动生成") }}</td></tr>
        </tbody>
      </table>
    </div>
    <p class="tbl-note">{{ $t("另外还有合规页面、已发内容、站内文章、已成交买家四项，一起算在 12 项里。") }}</p>
  </div>
</section>

<!-- ============ 02 30 天 ============ -->
<section class="sec">
  <div class="wrap">
    <div class="sec__head">
      <span class="eyebrow">{{ $t("最实在的一步") }}</span>
      <h2 class="sec__title">{{ $t("30 天，每天做什么已经写好了") }}</h2>
      <p class="sec__sub">{{ $t("体检完点一下「生成 30 天方案」，出来的不是一份「建议加强内容运营」的空话，而是 30 条具体任务：哪天做什么、为什么做、点开能直接跳到该改的页面。") }}</p>
    </div>

    <div class="grid4 wk">
      <article class="card wk__c">
        <div class="wk__hd"><span class="wk__n">01</span><svg class="ico"><use href="#i-bolt"/></svg></div>
        <h3 class="card__t">{{ $t("第 1 周 · 修地基") }}</h3>
        <p class="card__d">{{ $t("这周") }}<b>{{ $t("故意不产出任何对外内容") }}</b>{{ $t("。页面接不住流量，发了也是白花。先把 sitemap 交上去、异常价格改掉、商品归类、选出主推。") }}</p>
        <ul class="card__list"><li>{{ $t("提交 sitemap 与 Merchant Feed") }}</li><li>{{ $t("改掉汇率算错的价格") }}</li><li>{{ $t("主推的 10 个商品改本地语言") }}</li></ul>
      </article>

      <article class="card wk__c">
        <div class="wk__hd"><span class="wk__n">02</span><svg class="ico"><use href="#i-pin"/></svg></div>
        <h3 class="card__t">{{ $t("第 2 周 · 铺图钉") }}</h3>
        <p class="card__d">{{ $t("Pinterest 每天 2 个，一直排到第 14 天。商品主图现成，竖版 2:3 就是内容，不需要另外拍。") }}</p>
        <ul class="card__list"><li>{{ $t("零授权、零成本") }}</li><li>{{ $t("描述里带商品链接") }}</li><li>{{ $t("有买家的话加一封新品通知") }}</li></ul>
      </article>

      <article class="card wk__c">
        <div class="wk__hd"><span class="wk__n">03</span><svg class="ico"><use href="#i-book"/></svg></div>
        <h3 class="card__t">{{ $t("第 3 周 · 写长尾") }}</h3>
        <p class="card__d">{{ $t("站内博客 3 篇，每篇围绕一个真实搜索词，正文 800 字，中间内链到商品页。发出去会进 sitemap，慢慢带收录。") }}</p>
        <ul class="card__list"><li>{{ $t("「XX おすすめ」这种词") }}</li><li>{{ $t("一键发成站内文章") }}</li><li>{{ $t("比广告便宜，比广告久") }}</li></ul>
      </article>

      <article class="card wk__c">
        <div class="wk__hd"><span class="wk__n">04</span><svg class="ico"><use href="#i-chart"/></svg></div>
        <h3 class="card__t">{{ $t("第 4 周 · 复盘加码") }}</h3>
        <p class="card__d">{{ $t("看四个数：收录页数、站外访问、加购、订单。哪个渠道有访问就挪 70% 精力过去，其他的先停。") }}</p>
        <ul class="card__list"><li>{{ $t("收录没动 → 查抓取错误") }}</li><li>{{ $t("有点击没加购 → 补商品页细节") }}</li><li>{{ $t("有加购没下单 → 查运费和支付") }}</li></ul>
      </article>
    </div>

    <div class="plan-sample">
      <div class="plan-sample__hd">
        <b>{{ $t("第 1 周的实际任务") }}</b>
        <span>{{ $t("来自一份真实生成的方案 · 节奏和顺序都由店铺数据决定") }}</span>
      </div>
      <div class="tl">
        <div class="tl__i on">
          <div class="tl__t">{{ $t("把站点地图提交给 Google") }}</div>
          <p class="tl__d">{{ $t("打开 Search Console 提交 sitemap.xml；顺手在 Merchant Center 添加 feed/google.xml。这两步不花时间，但是后面所有流量的入口。") }}</p>
          <span class="tl__time">{{ $t("第 1 天 · 不花钱") }}</span>
        </div>
        <div class="tl__i on">
          <div class="tl__t">{{ $t("改掉异常价格") }}</div>
          <p class="tl__d">{{ $t("这些商品价格明显不对，多半是导入时汇率算错。价格离谱的页面，投多少流量都转化不了。") }}</p>
          <span class="tl__time">{{ $t("第 1 天 · 点开就到商品列表") }}</span>
        </div>
        <div class="tl__i">
          <div class="tl__t">{{ $t("选出这次主推的 10 个商品") }}</div>
          <p class="tl__d">{{ $t("系统按「有图、有详情、有 SEO 地址、价格正常、有库存」排过序。这 30 天只推这批，其余先不动。") }}</p>
          <span class="tl__time">{{ $t("第 3 天") }}</span>
        </div>
        <div class="tl__i">
          <div class="tl__t">{{ $t("主推商品的标题改成 Deutsche") }}</div>
          <p class="tl__d">{{ $t("只改主推那 10 个，不要 65 个一起改。标题里带上「是什么 + 给谁用 + 关键属性」——当地用户搜的是这个词，不是中文直译。") }}</p>
          <span class="tl__time">{{ $t("第 4 天 · 调翻译本地化官出稿") }}</span>
        </div>
        <div class="tl__i">
          <div class="tl__t">{{ $t("补主推商品的 SEO 描述") }}</div>
          <p class="tl__d">{{ $t("每个 80~120 字，写清楚谁会买、解决什么问题、有什么细节。这段话会直接出现在搜索结果里，比广告文案还重要。") }}</p>
          <span class="tl__time">{{ $t("第 5 天 · 调 SEO 优化师出稿") }}</span>
        </div>
        <div class="tl__i">
          <div class="tl__t">{{ $t("全站体检一遍") }}</div>
          <p class="tl__d">{{ $t("缺主图、缺描述、零库存、断链全部列出来，能当场修的就修掉。") }}</p>
          <span class="tl__time">{{ $t("第 6 天 · 调店铺巡检官") }}</span>
        </div>
      </div>
    </div>

    <div class="note note--wide">
      <svg class="ico"><use href="#i-shield"/></svg>
      <span>{{ $t("方案里的预期指标都标了推算依据，") }}<b>{{ $t("我们不承诺效果") }}</b>{{ $t("。0 基础的站，30 天内 0 单是正常的——这句话写在方案里，不是藏在条款里。") }}</span>
    </div>
  </div>
</section>

<!-- ============ 03 一件商品 9 个渠道 ============ -->
<section class="sec sec--gray">
  <div class="wrap">
    <div class="sec__head">
      <span class="eyebrow">{{ $t("内容产能") }}</span>
      <h2 class="sec__title">{{ $t("一件商品，铺到 9 个渠道") }}</h2>
      <p class="sec__sub">{{ $t("不是把同一段话复制九遍。Pinterest 要图钉描述，TikTok 要口播脚本，X 要短句，Google 商家要本地化措辞，站内博客要 800 字长文——这些是同一条卖点的九种写法。") }}</p>
    </div>

    <div class="fan">
      <div class="fan__src">
        <div class="fan__card">
          <span class="fan__img"></span>
          <div>
            <b>{{ $t("手冲咖啡滤杯 · 陶瓷") }}</b>
            <p>{{ $t("¥168 · 库存 42 · 已本地化") }}</p>
          </div>
        </div>
        <div class="fan__arrow">
          <svg class="ico"><use href="#i-arrow"/></svg>
          <span>{{ $t("选一件主推商品") }}</span>
        </div>
      </div>

      <div class="fan__out">
        <div class="fan__ch"><svg class="ico"><use href="#i-pin"/></svg><b>Pinterest</b><em>{{ $t("图钉描述") }}</em></div>
        <div class="fan__ch"><svg class="ico"><use href="#i-ig"/></svg><b>Instagram</b><em>{{ $t("配图短文") }}</em></div>
        <div class="fan__ch"><svg class="ico"><use href="#i-fb"/></svg><b>Facebook</b><em>{{ $t("帖子") }}</em></div>
        <div class="fan__ch"><svg class="ico"><use href="#i-x"/></svg><b>X</b><em>{{ $t("短句") }}</em></div>
        <div class="fan__ch"><svg class="ico"><use href="#i-tk"/></svg><b>TikTok</b><em>{{ $t("口播脚本") }}</em></div>
        <div class="fan__ch"><svg class="ico"><use href="#i-chat"/></svg><b>WhatsApp</b><em>{{ $t("私域话术") }}</em></div>
        <div class="fan__ch"><svg class="ico"><use href="#i-plane"/></svg><b>Telegram</b><em>{{ $t("频道稿") }}</em></div>
        <div class="fan__ch"><svg class="ico"><use href="#i-store"/></svg><b>Google 商家</b><em>{{ $t("本地文案") }}</em></div>
        <div class="fan__ch"><svg class="ico"><use href="#i-doc"/></svg><b>{{ $t("站内博客") }}</b><em>{{ $t("800 字长文") }}</em></div>
      </div>
    </div>

    <div class="grid3" style="margin-top:40px">
      <article class="card">
        <span class="ico-badge ico-badge--sm"><svg class="ico"><use href="#i-tag"/></svg></span>
        <h3 class="card__t">{{ $t("品牌名和固定标签带进每一篇") }}</h3>
        <p class="card__d">{{ $t("设一次，之后每一条都自动带上，不用每次重新交代语气。") }}</p>
      </article>
      <article class="card">
        <span class="ico-badge ico-badge--sm"><svg class="ico"><use href="#i-clock"/></svg></span>
        <h3 class="card__t">{{ $t("今天建议发哪个渠道") }}</h3>
        <p class="card__d">{{ $t("投递箱顶部直接给当天的排期，不用自己记 30 天日历。") }}</p>
      </article>
      <article class="card">
        <span class="ico-badge ico-badge--sm"><svg class="ico"><use href="#i-book"/></svg></span>
        <h3 class="card__t">{{ $t("博客可以一键发成站内文章") }}</h3>
        <p class="card__d">{{ $t("发布后进文章列表，被 sitemap 收录，成为长期的自然流量入口。") }}</p>
      </article>
    </div>

    <div class="note note--wide">
      <svg class="ico"><use href="#i-lock"/></svg>
      <span><b>{{ $t("说实话：文案是生成好的，发帖要你自己发。") }}</b>{{ $t("平台不碰你的社媒账号，也不会拿你的账号去群发——所以不需要把 FB / IG 授权给我们。需要自动发的只有站内博客和营销邮件，这两条走的是你自己的系统。") }}</span>
    </div>
  </div>
</section>

<!-- ============ 04 自动营销（深色·重点） ============ -->
<section class="sec sec--ink" id="automarketing">
  <div class="wrap">
    <div class="sec__head">
      <span class="eyebrow">{{ $t("这一页最重要的一段") }}</span>
      <h2 class="sec__title">{{ $t("自动营销：开了开关，它自己发") }}</h2>
      <p class="sec__sub">{{ $t("拉新是靠内容和渠道，一单一单攒的。但已经买过的人，不用再花钱去拉——他们就在你的订单里。自动营销干的就是这件不花广告费的事：按规则挑人、按场景写信、按额度慢慢发。") }}</p>
    </div>

    <div class="seq">
      <article class="seq__c">
        <div class="seq__hd">
          <span class="seq__ico"><svg class="ico"><use href="#i-bag"/></svg></span>
          <em class="seq__tag">{{ $t("自动") }}</em>
        </div>
        <h3 class="seq__t">{{ $t("新品通知") }}</h3>
        <p class="seq__d">{{ $t("近") }}<b>{{ $t("180 天") }}</b>{{ $t("买过的人，告诉他们刚上架的新品。上新了没人知道，等于没上。") }}</p>
        <p class="seq__w">{{ $t("圈人依据：已付款订单 + 最近购买时间") }}</p>
      </article>

      <article class="seq__c">
        <div class="seq__hd">
          <span class="seq__ico"><svg class="ico"><use href="#i-refresh"/></svg></span>
          <em class="seq__tag">{{ $t("自动") }}</em>
        </div>
        <h3 class="seq__t">{{ $t("弃购召回") }}</h3>
        <p class="seq__d">{{ $t("加了购物车") }}<b>{{ $t("2 小时") }}</b>{{ $t("还没下单，提醒回来结账；超过 7 天就不再发了——再久就没意义。") }}</p>
        <p class="seq__w">{{ $t("圈人依据：加车时间，2 小时 ~ 7 天之间") }}</p>
      </article>

      <article class="seq__c">
        <div class="seq__hd">
          <span class="seq__ico"><svg class="ico"><use href="#i-clock"/></svg></span>
          <em class="seq__tag">{{ $t("自动") }}</em>
        </div>
        <h3 class="seq__t">{{ $t("老客唤醒") }}</h3>
        <p class="seq__d">{{ $t("最后一单已经超过") }}<b>{{ $t("45 天") }}</b>{{ $t("，请他们回来看看。老客回购的成本，比拉新低得多。") }}</p>
        <p class="seq__w">{{ $t("圈人依据：最后一单时间超过 45 天") }}</p>
      </article>

      <article class="seq__c">
        <div class="seq__hd">
          <span class="seq__ico"><svg class="ico"><use href="#i-horn"/></svg></span>
          <em class="seq__tag seq__tag--m">{{ $t("手动") }}</em>
        </div>
        <h3 class="seq__t">{{ $t("促销群发") }}</h3>
        <p class="seq__d">{{ $t("所有下过单的顾客，按最近购买时间倒序。做活动时用，不适合天天发。") }}</p>
        <p class="seq__w">{{ $t("圈人依据：全部已付款买家") }}</p>
      </article>
    </div>

    <div class="frow frow--flip" style="margin-top:64px">
      <div>
        <span class="frow__tag">{{ $t("怕打扰？三个上限替你守住") }}</span>
        <h3 class="frow__t">{{ $t("宁可少发一封，也别被退订") }}</h3>
        <p class="frow__d">{{ $t("营销邮件发烂了比不发更糟——域名信誉一掉，连发货通知都进垃圾箱。所以这套系统的默认值偏保守，而且是") }}<b>{{ $t("你自己能改的") }}</b>{{ $t("。") }}</p>
        <ul class="frow__list">
          <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("每天最多发多少封，自己定（默认 200）") }}</span></li>
          <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("同一个人最少间隔多少天（默认 15 天）") }}</span></li>
          <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("同一条序列 7 天内不重复跑") }}</span></li>
          <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("退订的、最近刚发过的，自动排除") }}</span></li>
          <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("只圈付过款的人，不碰注册未下单的") }}</span></li>
        </ul>
      </div>

      <div class="frow__media">
        <div class="media-card media-card--ink">
          <div class="ctl">
            <div class="ctl__hd">
              <b>{{ $t("自动营销") }}</b>
              <span class="ctl__sw is-on"><i></i></span>
            </div>
            <div class="ctl__row">
              <span>{{ $t("每天最多发") }}</span>
              <b>200 <em>{{ $t("封") }}</em></b>
            </div>
            <div class="ctl__row">
              <span>{{ $t("同一人最少间隔") }}</span>
              <b>15 <em>{{ $t("天") }}</em></b>
            </div>
            <div class="ctl__row is-wrap">
              <span>{{ $t("自动跑哪些序列") }}</span>
              <div class="ctl__chips">
                <i class="is-on">{{ $t("新品通知") }}</i>
                <i class="is-on">{{ $t("弃购召回") }}</i>
                <i class="is-on">{{ $t("老客唤醒") }}</i>
                <i>{{ $t("促销群发") }}</i>
              </div>
            </div>
            <div class="ctl__ft">
              <div class="ctl__kpi"><b>1,284</b><span>{{ $t("累计送达") }}</span></div>
              <div class="ctl__kpi"><b>36</b><span>{{ $t("队列待发") }}</span></div>
              <div class="ctl__kpi"><b>0.4<em>%</em></b><span>{{ $t("退订率") }}</span></div>
            </div>
            <p class="ctl__note">{{ $t("定时任务每 10 分钟推一批。走你自己配的发信账号，退订链接自动带上。") }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="band band--inline">
      <div>
        <h2>{{ $t("自动营销不消耗 AI 积分") }}</h2>
        <p>{{ $t("自动跑的是内置模板，只有你点「AI 写一版」时才计费。所以就算每天发满，也不会偷偷烧额度。") }}</p>
      </div>
      <router-link class="btn btn--ghost" to="/pricing">{{ $t("看订阅怎么算") }}</router-link>
    </div>
  </div>
</section>

<!-- ============ 05 渠道优先级 ============ -->
<section class="sec">
  <div class="wrap">
    <div class="sec__head">
      <span class="eyebrow">{{ $t("别什么都做") }}</span>
      <h2 class="sec__title">{{ $t("先做哪个，先别做哪个") }}</h2>
      <p class="sec__sub">{{ $t("新店最容易犯的错，是五个平台一起开、每周都发、每条都没人看。系统会按你的店况直接排出优先级——包括明确告诉你「这个月先别做」。") }}</p>
    </div>

    <div class="tbl-wrap">
      <table class="tbl prio">
        <thead>
          <tr><th>{{ $t("渠道") }}</th><th>{{ $t("优先级") }}</th><th>{{ $t("为什么") }}</th><th>{{ $t("什么时候开始") }}</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ $t("站点地图 / Merchant Feed") }}</td>
            <td><span class="tagx tagx--main">{{ $t("主力") }}</span></td>
            <td>{{ $t("零授权、零成本，一次配好长期有效") }}</td>
            <td>{{ $t("立刻") }}</td>
          </tr>
          <tr>
            <td>{{ $t("站内 SEO（商品页）") }}</td>
            <td><span class="tagx tagx--main">{{ $t("主力") }}</span></td>
            <td>{{ $t("有复利，越早做越好") }}</td>
            <td>{{ $t("第 1 周") }}</td>
          </tr>
          <tr>
            <td>Pinterest</td>
            <td><span class="tagx tagx--main">{{ $t("主力") }}</span></td>
            <td>{{ $t("商品主图就是内容，零授权、可长期带访问") }}</td>
            <td>{{ $t("第 2 周") }}</td>
          </tr>
          <tr>
            <td>{{ $t("营销邮件（EDM）") }}</td>
            <td><span class="tagx tagx--main">{{ $t("主力") }}</span></td>
            <td>{{ $t("唯一能全自动的渠道，但要有买家才有收件人") }}</td>
            <td>{{ $t("第 2 周") }}</td>
          </tr>
          <tr>
            <td>{{ $t("站内博客") }}</td>
            <td><span class="tagx tagx--sub">{{ $t("辅助") }}</span></td>
            <td>{{ $t("见效慢，但长尾关键词能一直带流量") }}</td>
            <td>{{ $t("第 3 周") }}</td>
          </tr>
          <tr>
            <td>Facebook / Instagram</td>
            <td><span class="tagx tagx--no">{{ $t("暂不做") }}</span></td>
            <td>{{ $t("要账号授权，新号容易限流，性价比低于 Pinterest") }}</td>
            <td>{{ $t("后面再评估") }}</td>
          </tr>
          <tr>
            <td>TikTok / YouTube</td>
            <td><span class="tagx tagx--no">{{ $t("暂不做") }}</span></td>
            <td>{{ $t("要拍视频、API 需审核，这 30 天不划算") }}</td>
            <td>{{ $t("后面再评估") }}</td>
          </tr>
          <tr>
            <td>{{ $t("付费广告") }}</td>
            <td><span class="tagx tagx--bad">{{ $t("不建议") }}</span></td>
            <td>{{ $t("页面还没准备好，投了也转化不了") }}</td>
            <td>{{ $t("先不花这个钱") }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="tbl-note">{{ $t("这张表是按「有没有主图、描述铺没铺满、有没有买家」实时算的。你的店况不同，排出来的顺序也不同。") }}</p>
  </div>
</section>

<!-- ============ 06 员工目录 ============ -->
<section class="sec sec--gray" id="staff">
  <div class="wrap">
    <div class="sec__head">
      <span class="eyebrow">{{ $t("花名册") }}</span>
      <h2 class="sec__title">{{ $t("18 位员工，四组分工") }}</h2>
      <p class="sec__sub">{{ $t("前面讲的是「拉流量」这一条线。剩下的日常——采货、翻译、盯单、对账——也都有人在管。按位订阅，也可以买组合包。") }}</p>
    </div>

    <!-- 商品 -->
    <div class="staff__g">
      <div class="staff__gh">
        <span class="staff__gi"><svg class="ico"><use href="#i-box"/></svg></span>
        <h3>{{ $t("商品组") }}</h3>
        <em>{{ $t("5 位") }}</em>
        <p>{{ $t("把外面看到的东西，变成你店里能卖的东西。") }}</p>
      </div>
      <div class="staff__grid">
        <article class="staff">
          <b>{{ $t("商品采集官") }}</b>
          <p>{{ $t("从货源链接批量抓标题、属性、SKU 与图片，直接进已选商品，不用一条条复制。") }}</p>
          <span class="staff__st is-on">{{ $t("已接入") }}</span>
        </article>
        <article class="staff">
          <b>{{ $t("翻译本地化官") }}</b>
          <p>{{ $t("标题、属性、详情按目标市场语言本地化，不是逐字直译，保持当地习惯用词。") }}</p>
          <span class="staff__st is-on">{{ $t("已接入") }}</span>
        </article>
        <article class="staff">
          <b>{{ $t("SEO 优化师") }}</b>
          <p>{{ $t("按目标市场搜索习惯写标题、关键词、描述，写完直接入库。可以批量跑。") }}</p>
          <span class="staff__st is-on">{{ $t("已接入") }}</span>
        </article>
        <article class="staff">
          <b>{{ $t("图片处理官") }}</b>
          <p>{{ $t("主图上的外文一键换成本地语言，基础处理一起做，不用开设计软件。") }}</p>
          <span class="staff__st is-on">{{ $t("已接入") }}</span>
        </article>
        <article class="staff is-soon">
          <b>{{ $t("定价策略官") }}</b>
          <p>{{ $t("按采购价、运费、平台佣金与竞品价位算建议售价，避免拍脑袋定价。") }}</p>
          <span class="staff__st">{{ $t("接入中") }}</span>
        </article>
      </div>
    </div>

    <!-- 订单 -->
    <div class="staff__g">
      <div class="staff__gh">
        <span class="staff__gi"><svg class="ico"><use href="#i-truck"/></svg></span>
        <h3>{{ $t("订单组") }}</h3>
        <em>{{ $t("4 位") }}</em>
        <p>{{ $t("有人下单以后，别让人等、别让件丢。") }}</p>
      </div>
      <div class="staff__grid">
        <article class="staff">
          <b>{{ $t("订单管家") }}</b>
          <p>{{ $t("盯住待支付、待采购、待发货各环节，异常订单主动挑出来提醒处理。") }}</p>
          <span class="staff__st is-on">{{ $t("已接入") }}</span>
        </article>
        <article class="staff">
          <b>{{ $t("物流追踪官") }}</b>
          <p>{{ $t("批量跟进运单状态，卡在中转、清关超时、派送失败的件单独列出。") }}</p>
          <span class="staff__st is-on">{{ $t("已接入") }}</span>
        </article>
        <article class="staff">
          <b>{{ $t("客服应答官") }}</b>
          <p>{{ $t("按本店真实发货时效、支付方式与物流商，写一套买家常见问题话术，复制到你的客服渠道就能用。") }}</p>
          <span class="staff__st is-on">{{ $t("已接入") }}</span>
        </article>
        <article class="staff is-soon">
          <b>{{ $t("评论运营官") }}</b>
          <p>{{ $t("汇总商品评价，差评归因、话术建议，好评提炼成卖点。") }}</p>
          <span class="staff__st">{{ $t("接入中") }}</span>
        </article>
      </div>
    </div>

    <!-- 增长 -->
    <div class="staff__g staff__g--hot">
      <div class="staff__gh">
        <span class="staff__gi"><svg class="ico"><use href="#i-horn"/></svg></span>
        <h3>{{ $t("增长组") }}</h3>
        <em>{{ $t("5 位") }}</em>
        <p>{{ $t("负责「有人来」。前面三段讲的就是这一组。") }}</p>
      </div>
      <div class="staff__grid">
        <article class="staff">
          <b>{{ $t("推广总控") }}</b>
          <p>{{ $t("先体检找出真正卡住的地方，再给一份 30 天每天做什么的方案，点一下就调其他员工出稿。") }}</p>
          <span class="staff__st is-on">{{ $t("已接入") }}</span>
        </article>
        <article class="staff">
          <b>{{ $t("社媒内容官") }}</b>
          <p>{{ $t("把一件商品改写成 9 个渠道的成品，从 Pinterest 图钉到 TikTok 口播脚本。") }}</p>
          <span class="staff__st is-on">{{ $t("已接入") }}</span>
        </article>
        <article class="staff">
          <b>{{ $t("客户维系官") }}</b>
          <p>{{ $t("按人群圈选买家：新品通知、弃购召回、老客唤醒、促销群发，营销邮件自动分批触达。") }}</p>
          <span class="staff__st is-on">{{ $t("已接入") }}</span>
        </article>
        <article class="staff">
          <b>{{ $t("选品侦察兵") }}</b>
          <p>{{ $t("顺着你现在卖的品提相邻品类，用货源真实行情验证，每周给一份能落地的清单。") }}</p>
          <span class="staff__st is-on">{{ $t("已接入") }}</span>
        </article>
        <article class="staff">
          <b>{{ $t("促销策划官") }}</b>
          <p>{{ $t("按节令和积压库存给活动方案，方案确认后可以直接建券。") }}</p>
          <span class="staff__st is-on">{{ $t("已接入") }}</span>
        </article>
      </div>
    </div>

    <!-- 经营 -->
    <div class="staff__g">
      <div class="staff__gh">
        <span class="staff__gi"><svg class="ico"><use href="#i-chart"/></svg></span>
        <h3>{{ $t("经营组") }}</h3>
        <em>{{ $t("4 位") }}</em>
        <p>{{ $t("每天有人替你看数、对账、挑问题。") }}</p>
      </div>
      <div class="staff__grid">
        <article class="staff">
          <b>{{ $t("数据播报员") }}</b>
          <p>{{ $t("每天把访客、订单、转化、客单价整理成一段人话播报，不用自己拉表。") }}</p>
          <span class="staff__st is-on">{{ $t("已接入") }}</span>
        </article>
        <article class="staff">
          <b>{{ $t("财务对账官") }}</b>
          <p>{{ $t("订单收入和提成支出逐笔核对，找出漏记、重复扣款与异常提成。") }}</p>
          <span class="staff__st is-on">{{ $t("已接入") }}</span>
        </article>
        <article class="staff is-soon">
          <b>{{ $t("店铺巡检官") }}</b>
          <p>{{ $t("定期体检店铺：缺主图、缺描述、零库存、断链，一次性列出待修清单。") }}</p>
          <span class="staff__st">{{ $t("接入中") }}</span>
        </article>
        <article class="staff is-soon">
          <b>{{ $t("合规审查官") }}</b>
          <p>{{ $t("商品文案与图片过一遍违禁词、侵权与平台规则，降低下架封店风险。") }}</p>
          <span class="staff__st">{{ $t("接入中") }}</span>
        </article>
      </div>
    </div>

    <p class="tbl-note">{{ $t("标「接入中」的几位，平台侧能力已经就绪、店主端页面还在接。我们不在官网上把它们写成「马上能用」。") }}</p>
  </div>
</section>

<!-- ============ 07 组合包 ============ -->
<section class="sec">
  <div class="wrap">
    <div class="frow">
      <div>
        <span class="frow__tag">{{ $t("怎么买") }}</span>
        <h2 class="frow__t">{{ $t("按位买，或者一次买一组") }}</h2>
        <p class="frow__d">{{ $t("每一位员工可以单独订阅，也可以买组合包——组合包覆盖多位员工，折算下来单价更低。开通后到期时间按订阅算，快到期会提醒，不会突然停掉。") }}</p>
        <ul class="frow__list">
          <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("先用哪几位就开哪几位，不必一次全开") }}</span></li>
          <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("组合包覆盖的员工在服务市场里写清楚，不会含糊") }}</span></li>
          <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("到期前提醒；过期后是「续费」，不用重新找入口") }}</span></li>
        </ul>
        <p style="margin-top:24px"><router-link class="link-arrow" to="/pricing">{{ $t("看订阅和佣金怎么算") }} <i>→</i></router-link></p>
      </div>
      <div class="frow__media">
        <div class="media-card">
          <div class="bundle">
            <div class="bundle__hd">
              <b>{{ $t("增长组合包") }}</b>
              <span>{{ $t("覆盖 5 位员工") }}</span>
            </div>
            <ul class="bundle__list">
              <li><svg class="ico"><use href="#i-check"/></svg>{{ $t("推广总控") }}</li>
              <li><svg class="ico"><use href="#i-check"/></svg>{{ $t("社媒内容官") }}</li>
              <li><svg class="ico"><use href="#i-check"/></svg>{{ $t("客户维系官") }}</li>
              <li><svg class="ico"><use href="#i-check"/></svg>{{ $t("选品侦察兵") }}</li>
              <li><svg class="ico"><use href="#i-check"/></svg>{{ $t("促销策划官") }}</li>
            </ul>
            <div class="bundle__ft">
              <span>{{ $t("比单买省") }}</span>
              <router-link class="btn btn--primary btn--sm" to="/template-index">{{ $t("去服务市场") }}</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ============ 08 MCP ============ -->
<section class="sec sec--gray" id="mcp">
  <div class="wrap">
    <div class="mcp-box">
      <div class="mcp-box__l">
        <span class="frow__tag">{{ $t("接上你自己的 AI") }}</span>
        <h2 class="frow__t">{{ $t("豆包、ChatGPT、Claude 也能读这间店") }}</h2>
        <p class="frow__d">{{ $t("平台提供数据接口：AI 能查商品、价格、库存和推广方案，也能把写好的文案存回投递箱。") }}<b>{{ $t("不用把账号密码交给任何插件") }}</b>{{ $t("，也不用授权社媒账号。") }}</p>
        <ul class="frow__list">
          <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("读：商品、价格、库存、体检结果、30 天方案") }}</span></li>
          <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("写：把文案按渠道存回投递箱") }}</span></li>
          <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("不发：发帖这一步始终由你决定") }}</span></li>
        </ul>
      </div>
      <div class="mcp-box__r">
        <div class="code">
          <div class="code__hd"><i></i><i></i><i></i><span>MCP</span></div>
          <pre><code>shop.goods.list   →  65 个商品
shop.promo.plan   →  30 天方案
shop.content.save →  存回投递箱
shop.feed.google  →  商品 feed 地址</code></pre>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ============ 熊猫提示 + CTA ============ -->
<section class="sec sec--tight">
  <div class="wrap">
    <div class="pandy">
      <img class="pandy__m" src="/website/img/tao-think.png" alt="">
      <div>
        <p class="pandy__t">{{ $t("先开哪位？") }}</p>
        <p class="pandy__b">{{ $t("如果只开一位，开") }}<b>{{ $t("推广总控") }}</b>{{ $t("——它会体检、会排 30 天、还会叫其他员工干活。等订单多起来，再加客户维系官做自动营销。") }}</p>
        <router-link class="pandy__link" to="/pricing">{{ $t("看要花多少钱") }} →</router-link>
      </div>
    </div>
  </div>
</section>

<section class="sec sec--tight">
  <div class="wrap">
    <div class="cta">
      <div>
        <h2 class="cta__t">{{ $t("先让 AI 员工体检一遍你的店") }}</h2>
        <p class="cta__d">{{ $t("免费开店就能跑体检。先看堵点在哪，再决定花不花钱。") }}</p>
      </div>
      <div class="cta__btns">
        <router-link class="btn btn--primary" to="/sign-up">{{ $t("免费开店") }}</router-link>
        <router-link class="btn btn--ghost" to="/feature">{{ $t("看全部功能") }}</router-link>
      </div>
    </div>
  </div>
</section>

</main>
</template>

<script>
export default {
  name: 'WebsiteAiStaff',
}
</script>
